/* global globalThis */
import { createMutationId, isRetryableMutationError, mutationPayloadFingerprint } from './equipmentConcurrency';

const DATABASE_NAME = 'exreg-import-review';
const DATABASE_VERSION = 1;
const BATCH_SIZE = 25;
const clone = value => JSON.parse(JSON.stringify(value));
const checkpointKey = (scope, runKey) => JSON.stringify([scope, runKey]);
let databasePromise;
const activeRuns = new Set();

export const fingerprintImport = async value => {
  if (!globalThis.crypto?.subtle) throw new Error('Use a secure browser connection to save resumable imports.');
  const bytes = new TextEncoder().encode(typeof value === 'string' ? value : mutationPayloadFingerprint(value));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
};

const openDatabase = () => {
  if (!databasePromise) {
    databasePromise = new Promise((resolve, reject) => {
      if (!globalThis.indexedDB) {
        reject(new Error('This browser cannot save import progress. Enable browser storage before importing.'));
        return;
      }
      const request = globalThis.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        db.createObjectStore('runs', { keyPath: 'key' });
        const outcomes = db.createObjectStore('outcomes', { keyPath: 'key' });
        outcomes.createIndex('run', 'run', { unique: false });
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => { request.result.close(); databasePromise = null; };
        resolve(request.result);
      };
      request.onerror = () => reject(new Error('Import progress could not be opened in browser storage.'));
      request.onblocked = () => reject(new Error('Close other EXREG tabs before enabling resumable imports.'));
    }).catch(error => { databasePromise = null; throw error; });
  }
  return databasePromise;
};

// Metadata and the latest outcome are committed together. Outcomes are stored
// separately so a 10,000-row import never rewrites its complete report per item.
export const importCheckpointStore = {
  async load(scope, runKey) {
    const db = await openDatabase();
    const key = checkpointKey(scope, runKey);
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['runs', 'outcomes'], 'readonly');
      const meta = transaction.objectStore('runs').get(key);
      const results = transaction.objectStore('outcomes').index('run').getAll(key);
      transaction.oncomplete = () => resolve(meta.result
        ? { ...meta.result, outcomes: results.result.map(({ value }) => value).sort((a, b) => a.index - b.index) }
        : null);
      transaction.onerror = () => reject(new Error('Saved import progress could not be read.'));
      transaction.onabort = () => reject(new Error('Reading saved import progress was interrupted.'));
    });
  },
  async save(snapshot, outcome) {
    const db = await openDatabase();
    const key = checkpointKey(snapshot.scope, snapshot.runKey);
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['runs', 'outcomes'], 'readwrite');
      const { outcomes, ...metadata } = snapshot;
      transaction.objectStore('runs').put({ ...metadata, key });
      if (outcome) {
        transaction.objectStore('outcomes').put({ key: JSON.stringify([key, outcome.index]), run: key, value: outcome });
      }
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(new Error('Import progress could not be saved. Free browser storage, then resume this same file.'));
      transaction.onabort = () => reject(new Error('Saving import progress was interrupted. Resume this same file to reconcile the last request.'));
    });
  },
};

export const loadImportCheckpoint = (scope, runKey, store = importCheckpointStore) => store.load(scope, runKey);

const summarize = (outcomes, total) => {
  const counts = { imported: 0, skipped: 0, review: 0, failed: 0, processed: 0, total };
  outcomes.forEach(outcome => {
    if (Object.prototype.hasOwnProperty.call(counts, outcome.status)) counts[outcome.status] += 1;
    counts.processed += 1;
  });
  return counts;
};

const isRegisterContextChanged = error => error?.code === 'REGISTER_CONTEXT_CHANGED'
  || error?.response?.data?.code === 'REGISTER_CONTEXT_CHANGED';
const messageFor = error => error?.response?.data?.message || error?.message || 'The server rejected this record.';
const classifyFailure = error => {
  const code = error?.response?.data?.code;
  const message = messageFor(error);
  if (error?.response?.status === 409 && (code === 'EQUIPMENT_TAG_CONFLICT'
    || (!code && message === 'An equipment item with the same equipment_tag already exists.'))) {
    return { status: 'skipped', terminal: true, code: 'EQUIPMENT_TAG_CONFLICT', reason: `Equipment tag already exists in the register. ${message}` };
  }
  if (error?.response?.status === 409 && code === 'OLD_RFID_CONFLICT') {
    return { status: 'review', code: 'OLD_RFID_CONFLICT', reason: `RFID conflict: ${message}` };
  }
  return { status: 'failed', code: code || `HTTP_${error?.response?.status || 'UNKNOWN'}`, reason: message };
};

/**
 * Resume by original file identity, within one account/register. Imported and
 * deliberately skipped rows remain terminal. Corrected review/failed rows can
 * be retried without submitting the 10,000 previously imported rows again.
 *
 * The exact request is durable before dispatch. A lost response can therefore
 * be reconciled using the same mutation UUID, including after a page refresh.
 */
const executeReviewedImport = async ({
  review, scope, runKey, postItem, shouldStop = () => false,
  onProgress = () => {}, onBatch = () => {}, store = importCheckpointStore,
  mutationIdFactory = createMutationId,
}) => {
  if (!scope || !runKey) throw new Error('An account, register, and source file are required to resume an import.');
  if (!Array.isArray(review?.entries)) throw new Error('Review the source file before importing.');
  const previous = await loadImportCheckpoint(scope, runKey, store);
  if (previous && (previous.scope !== scope || previous.runKey !== runKey || previous.total !== review.entries.length)) {
    throw new Error('Saved progress belongs to a different source file or register.');
  }
  const outcomes = new Map((previous?.outcomes || []).map(outcome => [outcome.index, outcome]));
  let snapshot = {
    version: 1, scope, runKey, total: review.entries.length, nextIndex: 0,
    state: 'running', reason: null, pending: previous?.pending || null,
    startedAt: previous?.startedAt || new Date().toISOString(), updatedAt: new Date().toISOString(),
    counts: summarize([...outcomes.values()], review.entries.length),
  };
  let batch = [];
  let sinceUpdate = 0;
  const current = () => ({ ...snapshot, outcomes: [...outcomes.values()].sort((a, b) => a.index - b.index) });
  const publish = () => {
    const visible = current();
    onProgress(visible);
    if (batch.length) onBatch(batch, visible);
    batch = [];
    sinceUpdate = 0;
  };
  const save = async outcome => {
    snapshot.updatedAt = new Date().toISOString();
    // The store ignores outcomes; do not allocate a full report for each write.
    await store.save(snapshot, outcome);
  };
  const record = async outcome => {
    const replaced = outcomes.get(outcome.index);
    const beforeRecord = { ...snapshot, counts: { ...snapshot.counts } };
    outcomes.set(outcome.index, outcome);
    snapshot.counts = { ...snapshot.counts };
    if (replaced) snapshot.counts[replaced.status] -= 1;
    else snapshot.counts.processed += 1;
    snapshot.counts[outcome.status] += 1;
    snapshot.pending = null;
    snapshot.nextIndex = outcome.index + 1;
    try {
      await save(outcome);
    } catch (error) {
      // Keep the visible state aligned with the durable journal. In particular,
      // the exact pending request must stay locked after a failed outcome save.
      snapshot = beforeRecord;
      if (replaced) outcomes.set(outcome.index, replaced);
      else outcomes.delete(outcome.index);
      throw error;
    }
    batch.push(outcome);
    sinceUpdate += 1;
    if (sinceUpdate >= BATCH_SIZE) publish();
  };

  // Stop before other rows if a pending result cannot safely be reconciled with
  // the user's current choices. Never discard or silently replace that intent.
  if (snapshot.pending) {
    const entry = review.entries.find(item => item.index === snapshot.pending.index);
    if (!entry || entry.status !== 'ready'
      || mutationPayloadFingerprint(entry.values) !== snapshot.pending.fingerprint) {
      snapshot.state = 'waiting';
      snapshot.reason = 'A previous request is awaiting confirmation. Restore the original mapping and choices for that row, then resume before changing its values.';
      await save();
      publish();
      return current();
    }
  }
  await save();
  publish();
  try {
    const orderedEntries = snapshot.pending
      ? [review.entries.find(entry => entry.index === snapshot.pending.index), ...review.entries.filter(entry => entry.index !== snapshot.pending.index)]
      : review.entries;
    for (const entry of orderedEntries) {
      if (shouldStop()) {
        snapshot.state = 'paused';
        snapshot.reason = 'Import paused. Resume this same file to continue.';
        break;
      }
      const prior = outcomes.get(entry.index);
      if (prior?.status === 'imported' || (prior?.status === 'skipped' && prior.terminal !== false)) continue;
      snapshot.nextIndex = entry.index;
      const base = {
        index: entry.index, rowNumber: entry.rowNumber, tag: entry.tag,
        decisions: clone(entry.decisions || []), values: clone(entry.values || {}),
      };
      if (entry.status !== 'ready') {
        const outcome = {
          ...base, status: entry.status === 'skipped' ? 'skipped' : 'review', terminal: false,
          reason: (entry.issues || []).map(issue => issue.message).join(' ') || 'Review this source row before importing.',
        };
        if (!prior || mutationPayloadFingerprint(prior) !== mutationPayloadFingerprint(outcome)) await record(outcome);
        continue;
      }
      if (!snapshot.pending) {
        snapshot.pending = {
          index: entry.index, rowNumber: entry.rowNumber,
          fingerprint: mutationPayloadFingerprint(entry.values),
          payload: clone({ ...entry.values, mutation_id: mutationIdFactory() }),
        };
        await save();
      }
      const payload = snapshot.pending.payload;
      let lastError;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          await postItem(payload);
          lastError = null;
          break;
        } catch (error) {
          lastError = error;
          if (isRegisterContextChanged(error) || !isRetryableMutationError(error) || Number(error?.response?.status) === 429) break;
        }
      }
      if (!lastError) {
        await record({ ...base, status: 'imported', terminal: true, reason: 'Imported successfully.' });
      } else if (isRegisterContextChanged(lastError)) {
        snapshot.state = 'auth';
        snapshot.reason = 'Import paused because the active register changed. Return to the original account and register before resuming.';
        break;
      } else if (isRetryableMutationError(lastError)) {
        snapshot.state = 'waiting';
        snapshot.reason = Number(lastError?.response?.status) === 429
          ? 'The server requested a slower import. Wait briefly, then resume; the same request will be retried.'
          : 'The last request could not be confirmed. Resume to retry the exact request safely.';
        break;
      } else if ([401, 403].includes(Number(lastError?.response?.status))) {
        snapshot.state = 'auth';
        snapshot.reason = `Import paused: ${messageFor(lastError)} Sign in to the same account and register before resuming.`;
        break;
      } else {
        await record({ ...base, ...classifyFailure(lastError) });
      }
    }
    if (snapshot.state === 'running') {
      snapshot.state = 'complete';
      snapshot.nextIndex = review.entries.length;
    }
    await save();
    publish();
    return current();
  } catch (error) {
    // Storage failures must stop further writes. The durable pending request
    // from before dispatch is retained for the next attempt.
    snapshot.state = 'waiting';
    snapshot.reason = error.message || 'Import interrupted. Resume the same file to continue.';
    publish();
    throw error;
  }
};


export const runReviewedImport = async options => {
  const key = checkpointKey(options.scope, options.runKey);
  if (activeRuns.has(key)) throw new Error('This file is already being imported. Wait for it to pause or finish.');
  const execute = async () => {
    activeRuns.add(key);
    try { return await executeReviewedImport(options); }
    finally { activeRuns.delete(key); }
  };
  // Prevent a second browser tab from replacing the first tab's durable intent.
  if (globalThis.navigator?.locks?.request) {
    return globalThis.navigator.locks.request(`exreg-import:${key}`, { ifAvailable: true }, lock => {
      if (!lock) throw new Error('This file is being imported in another EXREG tab. Pause that import first.');
      return execute();
    });
  }
  return execute();
};
